import React, { ReactNode, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AuthContextValue, useAuth } from "../providers/AuthProvider";
import { Badge, Button, Drawer, Image, Menu, MenuProps, Popover } from "antd";
import {
  MailOutlined,
  AppstoreOutlined,
  SettingOutlined,
  DashboardOutlined,
  ShoppingCartOutlined,
  UserOutlined,
  FileProtectOutlined,
  EditOutlined,
  UsergroupAddOutlined,
  BankOutlined,
  HomeOutlined,
  BranchesOutlined,
  FileOutlined,
  FileTextOutlined,
  MenuUnfoldOutlined,
  ImportOutlined,
} from "@ant-design/icons";

import img from "../assets/images/logo-2.png";
import Header, { Voucher } from "../components/Header";
import { useRedemptionNotification, useSeen } from "../api/redemption";

interface props {
  children: ReactNode;
}

type MenuItem = Required<MenuProps>["items"][number];

const menuItemStyle = { paddingLeft: 5 };

const Layout: React.FC<props> = ({ children }) => {
  const navigate = useNavigate();
  const { logout: signOut, user } = useAuth() as AuthContextValue;

  const [menus, setMenus] = useState<MenuItem[]>([]);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [voucherList, setVoucherList] = useState<Voucher[]>([]);
  const { data: vouchers, isLoading, refetch } = useRedemptionNotification();
  const { mutateAsync: seen } = useSeen();

  const SHARED_MENUS: MenuItem[] = [
    {
      type: "divider",
    },
    {
      disabled: user?.status !== 1,
      key: "users",
      label: "My Users",
      icon: <UserOutlined />,
      style: menuItemStyle,
      onClick: () => navigate("/users"),
    },
  ];

  const OWNER_MENUES: MenuItem[] = [
    {
      key: "admins",
      label: "Admins",
      icon: <DashboardOutlined />,
      style: menuItemStyle,
      onClick: () => navigate("/sp-admin"),
      disabled: user?.status !== 1,
    },
  ];

  const SUPPER_ADMIN_MENUS: MenuItem[] = [
    {
      key: "dashboard",
      label: "Dashboard",
      icon: <DashboardOutlined />,
      style: menuItemStyle,
      onClick: () => navigate("/admin"),
      disabled: user?.status !== 1,
    },
    {
      key: "dalookupshboard",
      label: "Lookup",
      icon: (
        <svg
          fill="#ffffff"
          xmlns="http://www.w3.org/2000/svg"
          width="14px"
          height="14px"
          viewBox="0 0 52 52"
          enable-background="new 0 0 52 52"
          xmlSpace="preserve"
          stroke="#ffffff"
        >
          <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
          <g
            id="SVGRepo_tracerCarrier"
            stroke-linecap="round"
            stroke-linejoin="round"
          ></g>
          <g id="SVGRepo_iconCarrier">
            {" "}
            <g>
              {" "}
              <path d="M17.2,11.6h17.6c0.9,0,1.6-0.7,1.6-1.6V6.8c0-2.6-2.2-4.8-4.8-4.8H20.4c-2.6,0-4.8,2.2-4.8,4.8V10 C15.6,10.9,16.3,11.6,17.2,11.6z"></path>{" "}
              <path d="M43.6,6H42c-0.5,0-0.8,0.3-0.8,0.8V10c0,3.5-2.9,6.4-6.4,6.4H17.2c-3.5,0-6.4-2.9-6.4-6.4V6.8 C10.8,6.3,10.5,6,10,6H8.4c-2.6,0-4.8,2.2-4.8,4.8v34.4c0,2.6,2.2,4.8,4.8,4.8h35.2c2.6,0,4.8-2.2,4.8-4.8V10.8 C48.4,8.2,46.2,6,43.6,6z M37.5,44.1l-1,1c-0.3,0.3-0.7,0.3-1,0l-6.5-6.5c-1.9,1.3-4.2,2-6.7,1.6c-4.2-0.6-7.5-4.1-7.9-8.3 c-0.6-5.8,4.3-10.7,10.1-10.1c4.3,0.4,7.7,3.7,8.3,7.9c0.4,2.5-0.3,4.9-1.6,6.7l6.5,6.5C37.8,43.3,37.8,43.7,37.5,44.1z"></path>{" "}
              <path d="M23.5,24.8c-3.5,0-6.3,2.9-6.3,6.3c0,3.5,2.8,6.3,6.3,6.3c3.5,0,6.3-2.8,6.3-6.3 C29.8,27.6,27,24.8,23.5,24.8z"></path>{" "}
            </g>{" "}
          </g>
        </svg>
      ),
      style: menuItemStyle,
      onClick: () => navigate("/lookup"),
      disabled: user?.status !== 1,
    },
    {
      key: "agronomy",
      label: "Agronomy",
      icon: <ShoppingCartOutlined />,
      style: menuItemStyle,
      onClick: () => navigate("/agronomy"),
      disabled: user?.status !== 1,
    },
    {
      key: "inputs",
      label: "Input Products",
      icon: (
        <svg
          width="14px"
          height="14px"
          viewBox="0 0 28 28"
          version="1.1"
          xmlns="http://www.w3.org/2000/svg"
          xmlnsXlink="http://www.w3.org/1999/xlink"
          fill="#ffffff"
          stroke="#ffffff"
        >
          <g id="SVGRepo_bgCarrier" stroke-width="0" />

          <g
            id="SVGRepo_tracerCarrier"
            stroke-linecap="round"
            stroke-linejoin="round"
          />

          <g id="SVGRepo_iconCarrier">
            {" "}
            <title>input</title> <desc>Created with Sketch Beta.</desc>{" "}
            <defs> </defs>{" "}
            <g
              id="Page-1"
              stroke="none"
              stroke-width="1"
              fill="none"
              fill-rule="evenodd"
              type="MSPage"
            >
              {" "}
              <g
                id="Icon-Set"
                type="MSLayerGroup"
                transform="translate(-258.000000, -933.000000)"
                fill="#ffffff"
              >
                {" "}
                <path
                  d="M269,944 C269.001,943.445 268.555,942.999 268,943 C267.445,943.001 267.001,943.445 267,944 L267,951 C267,951.555 267.445,952.001 268,952 L275,952 C275.555,951.999 275.999,951.556 276,951 C276.001,950.444 275.555,949.999 275,950 L270.509,950 L285.293,935.217 C285.684,934.826 285.684,934.192 285.293,933.802 C284.902,933.412 284.269,933.412 283.879,933.802 L269,948.681 L269,944 L269,944 Z M284,946 L284,957 C284,958.087 283.086,959 282,959 L261.935,959.033 C260.848,959.033 259.967,958.152 259.967,957.065 L260,937 C260,935.913 260.914,935 262,935 L273,935 L273,933 L262,933 C259.827,933 258,935.221 258,937.394 L258,957.065 C258,959.238 259.762,961 261.935,961 L281.606,961 C283.779,961 286,959.173 286,957 L286,946 L284,946 L284,946 Z"
                  id="input"
                  type="MSShapeGroup"
                >
                  {" "}
                </path>{" "}
              </g>{" "}
            </g>{" "}
          </g>
        </svg>
      ),
      style: menuItemStyle,
      onClick: () => navigate("/inputs"),
      disabled: user?.status !== 1,
    },
    {
      key: "products",
      label: "Output Products",
      icon: (
        <svg
          width="14px"
          height="14px"
          viewBox="0 0 28 28"
          version="1.1"
          xmlns="http://www.w3.org/2000/svg"
          xmlnsXlink="http://www.w3.org/1999/xlink"
          // xml="http://www.bohemiancoding.com/sketch/ns" fill="#000000"
        >
          <g id="SVGRepo_bgCarrier" stroke-width="0" />

          <g
            id="SVGRepo_tracerCarrier"
            stroke-linecap="round"
            stroke-linejoin="round"
          />

          <g id="SVGRepo_iconCarrier">
            <title>output</title>
            <desc>Created with Sketch Beta.</desc>
            <defs> </defs>
            <g
              id="Page-1"
              stroke="none"
              stroke-width="1"
              fill="none"
              fill-rule="evenodd"
              type="MSPage"
            >
              <g
                id="Icon-Set"
                type="MSLayerGroup"
                transform="translate(-310.000000, -933.000000)"
                fill="#fff"
              >
                <path
                  d="M336,957 C336,958.087 335.087,959 334,959 L313.935,959.033 C312.848,959.033 311.967,958.152 311.967,957.065 L312,937 C312,935.913 312.913,935 314,935 L325,935 L325,933 L314,933 C311.827,933 310,935.221 310,937.394 L310,957.065 C310,959.238 311.762,961 313.935,961 L333.606,961 C335.779,961 338,959.173 338,957 L338,946 L336,946 L336,957 L336,957 Z M336.979,933 L330,933 C329.433,933.001 329.001,933.459 329,934 C328.999,934.541 329.433,935.001 330,935 L334.395,934.968 L319.308,949.357 C318.908,949.738 318.908,950.355 319.308,950.736 C319.706,951.117 320.354,951.117 320.753,950.736 L335.971,936.222 L336,941 C335.999,941.541 336.433,942.001 337,942 C337.567,941.999 337.999,941.541 338,941 L338,933.975 C338.001,933.434 337.546,932.999 336.979,933 L336.979,933 Z"
                  id="output"
                  type="MSShapeGroup"
                >
                  {" "}
                </path>
              </g>
            </g>
          </g>
        </svg>
      ),
      style: menuItemStyle,
      onClick: () => navigate("/products"),
      disabled: user?.status !== 1,
    },
    {
      key: "farming-stage",
      label: "Farming Stage",
      icon: (
        <svg
          className="w-6 h-6 text-gray-800 dark:text-white"
          aria-hidden="true"
          xmlns="http://www.w3.org/2000/svg"
          width="12"
          height="14"
          fill="none"
          viewBox="0 0 24 24"
        >
          <path
            stroke="currentColor"
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="1"
            d="M4 4.5V19a1 1 0 0 0 1 1h15M7 14l4-4 4 4 5-5m0 0h-3.207M20 9v3.207"
          />
        </svg>
      ),
      style: menuItemStyle,
      onClick: () => navigate("/farming-stage"),
      disabled: user?.status !== 1,
    },
    {
      key: "merchants",
      label: "Merchants",
      icon: (
        <svg
          width="14px"
          height="14px"
          viewBox="0 0 22 22"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          stroke="#ffffff"
        >
          <g id="SVGRepo_bgCarrier" stroke-width="0" />
          <g
            id="SVGRepo_tracerCarrier"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <g id="SVGRepo_iconCarrier">
            {" "}
            <path
              d="M15 14V17.6C15 18.4401 15 18.8601 14.8365 19.181C14.6927 19.4632 14.4632 19.6927 14.181 19.8365C13.8601 20 13.4401 20 12.6 20H7.40001C6.55994 20 6.1399 20 5.81903 19.8365C5.53679 19.6927 5.30731 19.4632 5.1635 19.181C5.00001 18.8601 5.00001 18.4401 5.00001 17.6V10M19 10V20M5.00001 16H15M5.55778 4.88446L3.5789 8.84223C3.38722 9.22559 3.29138 9.41727 3.3144 9.57308C3.3345 9.70914 3.40976 9.8309 3.52246 9.90973C3.65153 10 3.86583 10 4.29444 10H19.7056C20.1342 10 20.3485 10 20.4776 9.90973C20.5903 9.8309 20.6655 9.70914 20.6856 9.57308C20.7086 9.41727 20.6128 9.22559 20.4211 8.84223L18.4422 4.88446C18.2817 4.5634 18.2014 4.40287 18.0817 4.28558C17.9758 4.18187 17.8482 4.10299 17.7081 4.05465C17.5496 4 17.3701 4 17.0112 4H6.98887C6.62991 4 6.45043 4 6.29198 4.05465C6.15185 4.10299 6.02422 4.18187 5.91833 4.28558C5.79858 4.40287 5.71832 4.5634 5.55778 4.88446Z"
              stroke="#ffffff"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />{" "}
          </g>
        </svg>
      ),
      style: menuItemStyle,
      onClick: () => navigate("/organizations"),
      disabled: user?.status !== 1,
    },
    {
      key: "wareHouse",
      label: "Warehouse",
      icon: (
        <svg
          width="14px"
          height="14px"
          viewBox="0 0 22 22"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          stroke="#ffffff"
        >
          <g id="SVGRepo_bgCarrier" stroke-width="0" />
          <g
            id="SVGRepo_tracerCarrier"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <g id="SVGRepo_iconCarrier">
            {" "}
            <path
              d="M7 14.0014H17M7 14.0014V11.6014C7 11.0413 7 10.7613 7.10899 10.5474C7.20487 10.3592 7.35785 10.2062 7.54601 10.1104C7.75992 10.0014 8.03995 10.0014 8.6 10.0014H15.4C15.9601 10.0014 16.2401 10.0014 16.454 10.1104C16.6422 10.2062 16.7951 10.3592 16.891 10.5474C17 10.7613 17 11.0413 17 11.6014V14.0014M7 14.0014V18.0014V21.0014M17 14.0014V18.0014V21.0014M18.3466 6.17468L14.1466 4.07468C13.3595 3.68113 12.966 3.48436 12.5532 3.40691C12.1876 3.33832 11.8124 3.33832 11.4468 3.40691C11.034 3.48436 10.6405 3.68113 9.85338 4.07468L5.65337 6.17468C4.69019 6.65627 4.2086 6.89707 3.85675 7.25631C3.5456 7.574 3.30896 7.95688 3.16396 8.37725C3 8.85262 3 9.39106 3 10.4679V19.4014C3 19.9614 3 20.2414 3.10899 20.4554C3.20487 20.6435 3.35785 20.7965 3.54601 20.8924C3.75992 21.0014 4.03995 21.0014 4.6 21.0014H19.4C19.9601 21.0014 20.2401 21.0014 20.454 20.8924C20.6422 20.7965 20.7951 20.6435 20.891 20.4554C21 20.2414 21 19.9614 21 19.4014V10.4679C21 9.39106 21 8.85262 20.836 8.37725C20.691 7.95688 20.4544 7.574 20.1433 7.25631C19.7914 6.89707 19.3098 6.65627 18.3466 6.17468Z"
              stroke="#ffffff"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />{" "}
          </g>
        </svg>
      ),
      style: menuItemStyle,
      onClick: () => navigate("/warehouse"),
      disabled: user?.status !== 1,
    },
    {
      key: "work locations",
      label: "Work Locations",
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          className="lucide lucide-map-pin-check"
        >
          <path d="M19.43 12.935c.357-.967.57-1.955.57-2.935a8 8 0 0 0-16 0c0 4.993 5.539 10.193 7.399 11.799a1 1 0 0 0 1.202 0 32.197 32.197 0 0 0 .813-.728" />
          <circle cx="12" cy="10" r="3" />
          <path d="m16 18 2 2 4-4" />
        </svg>
      ),
      style: menuItemStyle,
      onClick: () => navigate("/workLocations"),
    },
    {
      key: "settings",
      label: "Price Settings",
      icon: (
        <svg
          className="mr-[-6px] w-6 h-6 text-gray-800 dark:text-white"
          aria-hidden="true"
          xmlns="http://www.w3.org/2000/svg"
          width="14"
          height="14"
          fill="none"
          viewBox="0 0 24 24"
        >
          <path
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M8 20V7m0 13-4-4m4 4 4-4m4-12v13m0-13 4 4m-4-4-4 4"
          />
        </svg>
      ),
      style: { marginLeft: -22 },
      children: [
        {
          key: "inputPrice",
          label: "Input Price",
          icon: (
            <svg
              transform="rotate(180 0 0)"
              className="w-6 h-6 text-gray-800  dark:text-white"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              width="14"
              height="14"
              fill="none"
              viewBox="0 0 24 24"
            >
              <path
                stroke="currentColor"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M16 12H4m12 0-4 4m4-4-4-4m3-4h2a3 3 0 0 1 3 3v10a3 3 0 0 1-3 3h-2"
              />
            </svg>
          ),
          style: { ...menuItemStyle, marginLeft: 50, width: 150 },
          onClick: () => navigate("/input-price"),
        },
        {
          key: "output price",
          label: "Output Price",
          icon: (
            <svg
              className="w-6 h-6 text-gray-800  dark:text-white"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              fill="none"
              viewBox="0 0 24 24"
            >
              <path
                stroke="currentColor"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M16 12H4m12 0-4 4m4-4-4-4m3-4h2a3 3 0 0 1 3 3v10a3 3 0 0 1-3 3h-2"
              />
            </svg>
          ),
          style: { ...menuItemStyle, marginLeft: 50, width: 150 },
          onClick: () => navigate("/output-price"),
        },
      ],
      // onClick: () => navigate('/organizations'),
      disabled: user?.status !== 1,
    },
    ...SHARED_MENUS,
    {
      disabled: user?.status !== 1,
      key: "all users",
      label: "All Users",
      icon: <UsergroupAddOutlined />,
      style: menuItemStyle,
      onClick: () => navigate("/all-users"),
    },
  ];

  const KFT_OPERATION_USER_MENUS: MenuItem[] = [
    {
      key: "dashboard",
      label: "Dashboard",
      icon: <DashboardOutlined />,
      style: menuItemStyle,
      onClick: () => navigate("/admin"),
      disabled: user?.status !== 1,
    },
    {
      key: "orders",
      label: "Orders",
      icon: <ShoppingCartOutlined />,
      style: menuItemStyle,
      onClick: () => navigate("/orders"),
      disabled: user?.status !== 1,
    },
    {
      key: "farmers",
      label: "Farmers",
      icon: (
        <svg
          width="20px"
          height="20px"
          viewBox="0 0 400 400"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          stroke="#fff"
        >
          <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
          <g
            id="SVGRepo_tracerCarrier"
            stroke-linecap="round"
            stroke-linejoin="round"
          ></g>
          <g id="SVGRepo_iconCarrier">
            {" "}
            <path
              d="M69 150.936C144.654 111.512 218.822 115.319 300.542 136.059"
              stroke="#fff"
              stroke-opacity="0.9"
              stroke-width="16"
              stroke-linecap="round"
              stroke-linejoin="round"
            ></path>{" "}
            <path
              d="M108.01 121.189C143.403 6.75972 229.61 30.2203 260.274 122"
              stroke="#fff"
              stroke-opacity="0.9"
              stroke-width="16"
              stroke-linecap="round"
              stroke-linejoin="round"
            ></path>{" "}
            <path
              d="M111 161C95.0002 161.56 111 177.233 121 185C111 280 257 278 247 178C253.667 172 263 159.2 247 156"
              stroke="#fff"
              stroke-opacity="0.9"
              stroke-width="16"
              stroke-linecap="round"
              stroke-linejoin="round"
            ></path>{" "}
            <path
              d="M165 174C165 170.678 165 167.332 165 164"
              stroke="#fff"
              stroke-opacity="0.9"
              stroke-width="16"
              stroke-linecap="round"
              stroke-linejoin="round"
            ></path>{" "}
            <path
              d="M202 174C199.863 170.684 200.002 167.36 200.002 164"
              stroke="#fff"
              stroke-opacity="0.9"
              stroke-width="16"
              stroke-linecap="round"
              stroke-linejoin="round"
            ></path>{" "}
            <path
              d="M198.684 211C190.46 217.142 177.038 217.142 171 213.783"
              stroke="#fff"
              stroke-opacity="0.9"
              stroke-width="16"
              stroke-linecap="round"
              stroke-linejoin="round"
            ></path>{" "}
            <path
              d="M81.6422 343.665C78.0834 239.939 236.75 244.476 262.245 283.163C275.092 302.657 277.633 332.199 281.665 356"
              stroke="#fff"
              stroke-opacity="0.9"
              stroke-width="16"
              stroke-linecap="round"
              stroke-linejoin="round"
            ></path>{" "}
            <path
              d="M132.212 266.677C131.918 278.857 132.397 291.033 132.975 303.161"
              stroke="#fff"
              stroke-opacity="0.9"
              stroke-width="16"
              stroke-linecap="round"
              stroke-linejoin="round"
            ></path>{" "}
            <path
              d="M135.693 307.774C164.524 304.109 196.079 310.137 223.779 308.152"
              stroke="#fff"
              stroke-opacity="0.9"
              stroke-width="16"
              stroke-linecap="round"
              stroke-linejoin="round"
            ></path>{" "}
            <path
              d="M230.188 274.226C224.319 299.554 229.685 324.313 229.685 349.71"
              stroke="#fff"
              stroke-opacity="0.9"
              stroke-width="16"
              stroke-linecap="round"
              stroke-linejoin="round"
            ></path>{" "}
            <path
              d="M132.097 285.548C134.539 306.642 132.727 328.854 135.517 349.71"
              stroke="#fff"
              stroke-opacity="0.9"
              stroke-width="16"
              stroke-linecap="round"
              stroke-linejoin="round"
            ></path>{" "}
            <path
              d="M253.604 197.254C241.259 328.583 363.287 276.062 324.272 191.193"
              stroke="#fff"
              stroke-opacity="0.9"
              stroke-width="16"
              stroke-linecap="round"
              stroke-linejoin="round"
            ></path>{" "}
            <path
              d="M290.423 197.484C290.649 250.303 290.423 303.501 290.423 356"
              stroke="#fff"
              stroke-opacity="0.9"
              stroke-width="16"
              stroke-linecap="round"
              stroke-linejoin="round"
            ></path>{" "}
          </g>
        </svg>
      ),
      style: menuItemStyle,
      onClick: () => navigate("/customers"),
      disabled: user?.status !== 1,
    },
    {
      key: "contract",
      label: "Contracts",
      icon: <FileOutlined />,
      style: menuItemStyle,
      onClick: () => navigate("/contracts"),
      disabled: user?.status !== 1,
    },
    {
      key: "voucher",
      label: "Vouchers",
      icon: <FileTextOutlined />,
      style: menuItemStyle,
      onClick: () => navigate("/voucher"),
      disabled: user?.status !== 1,
    },
    // {
    //   key: "import",
    //   label: "Import",
    //   icon: <ImportOutlined />,
    //   style: menuItemStyle,
    //   onClick: () => navigate("/importCustomers"),
    //   disabled: user?.status !== 1,
    // },
    // {
    //     key: 'report',
    //     label: 'Reports',
    //     icon: <BankOutlined />,
    //     style: menuItemStyle,
    //     onClick: () => navigate('/organizations'),
    //     disabled: user?.status !== 1,
    // },
  ];

  const OFF_TAKER_ADMIN_MENUS: MenuItem[] = [
    {
      key: "dashboard",
      label: "Dashboard",
      icon: <DashboardOutlined />,
      style: menuItemStyle,
      onClick: () => navigate("/off-taker"),
      disabled: Number(user?.status) !== 1,
    },
    {
      key: "orders",
      label: "Orders",
      icon: <ShoppingCartOutlined />,
      style: menuItemStyle,
      onClick: () => navigate("/my-orders"),
      disabled: Number(user?.status) !== 1,
    },
    {
      key: "products",
      label: "Products",
      icon: <AppstoreOutlined />,
      style: menuItemStyle,
      onClick: () => navigate("/products"),
      disabled: Number(user?.status) !== 1,
    },
    {
      disabled: user?.status !== 1,
      key: "branches",
      label: "Branches",
      icon: <BranchesOutlined />,
      style: menuItemStyle,
      onClick: () => navigate("/branches"),
    },
    ...SHARED_MENUS,
    {
      key: "documents",
      label: "Documents",
      icon: <FileProtectOutlined />,
      style: menuItemStyle,
      // disabled: user?.status !== 1,
      onClick: () => navigate("/documents"),
    },
  ];

  const AGRO_DEALER_ADMIN_MENUS: MenuItem[] = [
    {
      key: "dashboard",
      label: "Dashboard",
      icon: <DashboardOutlined />,
      style: menuItemStyle,
      onClick: () => navigate("/ad-dashboard"),
      disabled: user?.status !== 1,
    },
    {
      key: "vouchers",
      label: "Vouchers",
      icon: <FileTextOutlined />,
      style: menuItemStyle,
      disabled: user?.status !== 1,
      onClick: () => navigate("/voucher"),
    },
    {
      key: "input-sales",
      label: "Input Sales",
      icon: <FileProtectOutlined />,
      style: menuItemStyle,
      disabled: user?.status !== 1,
      onClick: () => navigate("/input-sales"),
    },
    {
      key: "redemption",
      label: "Payment",
      icon: <FileTextOutlined />,
      style: menuItemStyle,
      onClick: () => navigate("/redemptions"),
      disabled: user?.status !== 1,
    },
    {
      disabled: user?.status !== 1,
      key: "branches",
      label: "Stores",
      icon: <BranchesOutlined />,
      style: menuItemStyle,
      onClick: () => navigate("/branches"),
    },
    ...SHARED_MENUS,
    {
      key: "documents",
      label: "Documents",
      icon: <FileProtectOutlined />,
      style: menuItemStyle,
      onClick: () => navigate("/documents"),
    },
  ];

  const AGRO_DEALER_OPERATION_MENUS: MenuItem[] = [
    {
      key: "dashboard",
      label: "Dashboard",
      icon: <DashboardOutlined />,
      style: menuItemStyle,
      onClick: () => navigate("/ad-dashboard"),
      disabled: user?.status !== 1,
    },
    {
      key: "vouchers",
      label: "Vouchers",
      icon: <FileTextOutlined />,
      style: menuItemStyle,
      disabled: user?.status !== 1,
      onClick: () => navigate("/voucher"),
    },
    {
      key: "input-sales",
      label: "Input Sales",
      icon: <FileProtectOutlined />,
      style: menuItemStyle,
      disabled: user?.status !== 1,
      onClick: () => navigate("/input-sales"),
    },
  ];

  const AGRO_DEALER_STORE_MENUS: MenuItem[] = [
    {
      key: "redemption",
      label: "Redemption",
      icon: <DashboardOutlined />,
      style: menuItemStyle,
      onClick: () => navigate("/redemption"),
      // disabled: user?.status !== 1,
    },
  ];

  const BANK_ADMIN_MENUS: MenuItem[] = [
    {
      key: "dashboard",
      label: "Dashboard",
      icon: <DashboardOutlined />,
      style: menuItemStyle,
      onClick: () => navigate("/mfi"),
      disabled: user?.status !== 1,
    },
    {
      key: "farmers",
      label: "Farmers",
      icon: (
        <svg
          width="20px"
          height="20px"
          viewBox="0 0 400 400"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          stroke="#fff"
        >
          <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
          <g
            id="SVGRepo_tracerCarrier"
            stroke-linecap="round"
            stroke-linejoin="round"
          ></g>
          <g id="SVGRepo_iconCarrier">
            {" "}
            <path
              d="M69 150.936C144.654 111.512 218.822 115.319 300.542 136.059"
              stroke="#fff"
              stroke-opacity="0.9"
              stroke-width="16"
              stroke-linecap="round"
              stroke-linejoin="round"
            ></path>{" "}
            <path
              d="M108.01 121.189C143.403 6.75972 229.61 30.2203 260.274 122"
              stroke="#fff"
              stroke-opacity="0.9"
              stroke-width="16"
              stroke-linecap="round"
              stroke-linejoin="round"
            ></path>{" "}
            <path
              d="M111 161C95.0002 161.56 111 177.233 121 185C111 280 257 278 247 178C253.667 172 263 159.2 247 156"
              stroke="#fff"
              stroke-opacity="0.9"
              stroke-width="16"
              stroke-linecap="round"
              stroke-linejoin="round"
            ></path>{" "}
            <path
              d="M165 174C165 170.678 165 167.332 165 164"
              stroke="#fff"
              stroke-opacity="0.9"
              stroke-width="16"
              stroke-linecap="round"
              stroke-linejoin="round"
            ></path>{" "}
            <path
              d="M202 174C199.863 170.684 200.002 167.36 200.002 164"
              stroke="#fff"
              stroke-opacity="0.9"
              stroke-width="16"
              stroke-linecap="round"
              stroke-linejoin="round"
            ></path>{" "}
            <path
              d="M198.684 211C190.46 217.142 177.038 217.142 171 213.783"
              stroke="#fff"
              stroke-opacity="0.9"
              stroke-width="16"
              stroke-linecap="round"
              stroke-linejoin="round"
            ></path>{" "}
            <path
              d="M81.6422 343.665C78.0834 239.939 236.75 244.476 262.245 283.163C275.092 302.657 277.633 332.199 281.665 356"
              stroke="#fff"
              stroke-opacity="0.9"
              stroke-width="16"
              stroke-linecap="round"
              stroke-linejoin="round"
            ></path>{" "}
            <path
              d="M132.212 266.677C131.918 278.857 132.397 291.033 132.975 303.161"
              stroke="#fff"
              stroke-opacity="0.9"
              stroke-width="16"
              stroke-linecap="round"
              stroke-linejoin="round"
            ></path>{" "}
            <path
              d="M135.693 307.774C164.524 304.109 196.079 310.137 223.779 308.152"
              stroke="#fff"
              stroke-opacity="0.9"
              stroke-width="16"
              stroke-linecap="round"
              stroke-linejoin="round"
            ></path>{" "}
            <path
              d="M230.188 274.226C224.319 299.554 229.685 324.313 229.685 349.71"
              stroke="#fff"
              stroke-opacity="0.9"
              stroke-width="16"
              stroke-linecap="round"
              stroke-linejoin="round"
            ></path>{" "}
            <path
              d="M132.097 285.548C134.539 306.642 132.727 328.854 135.517 349.71"
              stroke="#fff"
              stroke-opacity="0.9"
              stroke-width="16"
              stroke-linecap="round"
              stroke-linejoin="round"
            ></path>{" "}
            <path
              d="M253.604 197.254C241.259 328.583 363.287 276.062 324.272 191.193"
              stroke="#fff"
              stroke-opacity="0.9"
              stroke-width="16"
              stroke-linecap="round"
              stroke-linejoin="round"
            ></path>{" "}
            <path
              d="M290.423 197.484C290.649 250.303 290.423 303.501 290.423 356"
              stroke="#fff"
              stroke-opacity="0.9"
              stroke-width="16"
              stroke-linecap="round"
              stroke-linejoin="round"
            ></path>{" "}
          </g>
        </svg>
      ),
      style: menuItemStyle,
      onClick: () => navigate("/customers"),
      disabled: user?.status !== 1,
    },
    {
      key: "contract",
      label: "Contracts",
      icon: <FileOutlined />,
      style: menuItemStyle,
      onClick: () => navigate("/contracts"),
      disabled: user?.status !== 1,
    },
    {
      key: "voucher",
      label: "Vouchers",
      icon: <FileTextOutlined />,
      style: menuItemStyle,
      onClick: () => navigate("/voucher"),
      disabled: user?.status !== 1,
    },
    {
      key: "redemption",
      label: "Payment",
      icon: <FileTextOutlined />,
      style: menuItemStyle,
      onClick: () => navigate("/redemptions"),
      disabled: user?.status !== 1,
    },
    {
      disabled: user?.status !== 1,
      key: "branches",
      label: "Branches",
      icon: <BranchesOutlined />,
      style: menuItemStyle,
      onClick: () => navigate("/branches"),
    },
    ...SHARED_MENUS,
    {
      key: "documents",
      label: "Documents",
      icon: <FileProtectOutlined />,
      style: menuItemStyle,
      disabled: user?.isFirstLogin === 1,
      onClick: () => navigate("/documents"),
    },
  ];

  const BANK_OPERATION_MENUS: MenuItem[] = [
    {
      key: "dashboard",
      label: "Dashboard",
      icon: <DashboardOutlined />,
      style: menuItemStyle,
      onClick: () => navigate("/mfi"),
      disabled: user?.status !== 1,
    },
    {
      key: "farmers",
      label: "Farmers",
      icon: (
        <svg
          width="20px"
          height="20px"
          viewBox="0 0 400 400"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          stroke="#fff"
        >
          <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
          <g
            id="SVGRepo_tracerCarrier"
            stroke-linecap="round"
            stroke-linejoin="round"
          ></g>
          <g id="SVGRepo_iconCarrier">
            {" "}
            <path
              d="M69 150.936C144.654 111.512 218.822 115.319 300.542 136.059"
              stroke="#fff"
              stroke-opacity="0.9"
              stroke-width="16"
              stroke-linecap="round"
              stroke-linejoin="round"
            ></path>{" "}
            <path
              d="M108.01 121.189C143.403 6.75972 229.61 30.2203 260.274 122"
              stroke="#fff"
              stroke-opacity="0.9"
              stroke-width="16"
              stroke-linecap="round"
              stroke-linejoin="round"
            ></path>{" "}
            <path
              d="M111 161C95.0002 161.56 111 177.233 121 185C111 280 257 278 247 178C253.667 172 263 159.2 247 156"
              stroke="#fff"
              stroke-opacity="0.9"
              stroke-width="16"
              stroke-linecap="round"
              stroke-linejoin="round"
            ></path>{" "}
            <path
              d="M165 174C165 170.678 165 167.332 165 164"
              stroke="#fff"
              stroke-opacity="0.9"
              stroke-width="16"
              stroke-linecap="round"
              stroke-linejoin="round"
            ></path>{" "}
            <path
              d="M202 174C199.863 170.684 200.002 167.36 200.002 164"
              stroke="#fff"
              stroke-opacity="0.9"
              stroke-width="16"
              stroke-linecap="round"
              stroke-linejoin="round"
            ></path>{" "}
            <path
              d="M198.684 211C190.46 217.142 177.038 217.142 171 213.783"
              stroke="#fff"
              stroke-opacity="0.9"
              stroke-width="16"
              stroke-linecap="round"
              stroke-linejoin="round"
            ></path>{" "}
            <path
              d="M81.6422 343.665C78.0834 239.939 236.75 244.476 262.245 283.163C275.092 302.657 277.633 332.199 281.665 356"
              stroke="#fff"
              stroke-opacity="0.9"
              stroke-width="16"
              stroke-linecap="round"
              stroke-linejoin="round"
            ></path>{" "}
            <path
              d="M132.212 266.677C131.918 278.857 132.397 291.033 132.975 303.161"
              stroke="#fff"
              stroke-opacity="0.9"
              stroke-width="16"
              stroke-linecap="round"
              stroke-linejoin="round"
            ></path>{" "}
            <path
              d="M135.693 307.774C164.524 304.109 196.079 310.137 223.779 308.152"
              stroke="#fff"
              stroke-opacity="0.9"
              stroke-width="16"
              stroke-linecap="round"
              stroke-linejoin="round"
            ></path>{" "}
            <path
              d="M230.188 274.226C224.319 299.554 229.685 324.313 229.685 349.71"
              stroke="#fff"
              stroke-opacity="0.9"
              stroke-width="16"
              stroke-linecap="round"
              stroke-linejoin="round"
            ></path>{" "}
            <path
              d="M132.097 285.548C134.539 306.642 132.727 328.854 135.517 349.71"
              stroke="#fff"
              stroke-opacity="0.9"
              stroke-width="16"
              stroke-linecap="round"
              stroke-linejoin="round"
            ></path>{" "}
            <path
              d="M253.604 197.254C241.259 328.583 363.287 276.062 324.272 191.193"
              stroke="#fff"
              stroke-opacity="0.9"
              stroke-width="16"
              stroke-linecap="round"
              stroke-linejoin="round"
            ></path>{" "}
            <path
              d="M290.423 197.484C290.649 250.303 290.423 303.501 290.423 356"
              stroke="#fff"
              stroke-opacity="0.9"
              stroke-width="16"
              stroke-linecap="round"
              stroke-linejoin="round"
            ></path>{" "}
          </g>
        </svg>
      ),
      style: menuItemStyle,
      onClick: () => navigate("/customers"),
      disabled: user?.status !== 1,
    },
    {
      key: "contract",
      label: "Contract",
      icon: <EditOutlined />,
      style: menuItemStyle,
      onClick: () => navigate("/contracts"),
      disabled: user?.status !== 1,
    },
    {
      key: "voucher",
      label: "Vouchers",
      icon: <FileTextOutlined />,
      style: menuItemStyle,
      onClick: () => navigate("/voucher"),
      disabled: user?.status !== 1,
    },
    {
      key: "redemption",
      label: "Redemption",
      icon: <FileTextOutlined />,
      style: menuItemStyle,
      onClick: () => navigate("/redemption"),
      disabled: user?.status !== 1,
    },
    {
      disabled: user?.status !== 1,
      key: "branches",
      label: "Branches",
      icon: <BranchesOutlined />,
      style: menuItemStyle,
      onClick: () => navigate("/branches"),
    },
    {
      key: "inputSales",
      label: "Input Sales",
      icon: <FileProtectOutlined />,
      style: menuItemStyle,
      onClick: () => navigate("/documents"),
      disabled: user?.isFirstLogin === 1,
    },
    // ...SHARED_MENUS,
  ];

  const INSURANCE_ADMIN_MENUS: MenuItem[] = [
    {
      key: "dashboard",
      label: "Dashboard",
      icon: <DashboardOutlined />,
      style: menuItemStyle,
      onClick: () => navigate("/insurance"),
      disabled: user?.status !== 1,
    },
    {
      key: "contracts",
      label: "Contracts",
      icon: <EditOutlined />,
      style: menuItemStyle,
      onClick: () => navigate("/contracts"),
      disabled: user?.status !== 1,
    },
    {
      disabled: user?.status !== 1,
      key: "branches",
      label: "Branches",
      icon: <BranchesOutlined />,
      style: menuItemStyle,
      onClick: () => navigate("/branches"),
    },
    ...SHARED_MENUS,
    {
      key: "documents",
      label: "Documents",
      icon: <FileProtectOutlined />,
      style: menuItemStyle,
      disabled: user?.isFirstLogin === 1,
      onClick: () => navigate("/documents"),
    },
  ];

  useEffect(() => {
    if (user && user.roleName) {
      if (user.roleName === "ROLE_SUPER_ADMIN") {
        setMenus(OWNER_MENUES);
      } else if (user.roleName === "ROLE_KFT_SUPER_ADMIN") {
        setMenus(SUPPER_ADMIN_MENUS);
      } else if (user.roleName === "ROLE_KFT_SP_ADMIN") {
        setMenus(KFT_OPERATION_USER_MENUS);
      } else if (user.roleName === "ROLE_OFF_TAKER_ADMIN") {
        setMenus(OFF_TAKER_ADMIN_MENUS);
      } else if (user.roleName === "ROLE_MPC_ADMIN") {
        setMenus(AGRO_DEALER_ADMIN_MENUS);
      } else if (user.roleName === "ROLE_MPC_OPERATION") {
        setMenus(AGRO_DEALER_OPERATION_MENUS);
      } else if (user.roleName === "MOB_ROLE_AGRO_DEALER") {
        setMenus(AGRO_DEALER_STORE_MENUS);
      } else if (user.roleName === "ROLE_MFI_ADMIN") {
        setMenus(BANK_ADMIN_MENUS);
      } else if (user.roleName === "ROLE_MFI_OPERATION") {
        setMenus(BANK_ADMIN_MENUS);
      } else if (user.roleName === "INSURANCE_ADMIN") {
        setMenus(INSURANCE_ADMIN_MENUS);
      } else if (user.roleName === "AGRO_DEALER_ADMIN") {
        setMenus(AGRO_DEALER_ADMIN_MENUS);
      }
    } else {
    }
  }, [user]);

  const onClick: MenuProps["onClick"] = (e) => {};

  const handleSeeMoreClick = () => {
    setIsPopoverOpen(false);
    navigate("/voucher?status=redeemed");
  };

  useEffect(() => {
    if (vouchers?.data?.data) {
      const transformedVouchers = vouchers.data.data.map(
        (item: {
          id: number;
          messageId: number;
          title: string;
          seen: boolean;
          description: string;
          createdAt: string;
        }) => ({
          id: item.id,
          messageId: item.messageId,
          title: item.title,
          isSeen: item.seen ? 1 : 0,
          description: item.description,
          createdAt: item.createdAt,
        })
      );
      setVoucherList(transformedVouchers);
    }
  }, [vouchers]);

  // Filter vouchers by status (unread - isSeen === 0)
  const vouchersWithStatusRedeemed = useMemo(
    () => voucherList.filter((voucher) => voucher.isSeen === 0),
    [voucherList]
  );

  // Sort vouchers by createdAt (handling the date format correctly)
  const sortedVouchers = useMemo(
    () =>
      vouchersWithStatusRedeemed.sort((a, b) => {
        const dateA = new Date(a.createdAt.split("/").reverse().join("-")); // Convert to yyyy-MM-dd
        const dateB = new Date(b.createdAt.split("/").reverse().join("-"));
        return dateB.getTime() - dateA.getTime();
      }),
    [vouchersWithStatusRedeemed]
  );

  // Select the latest 5 vouchers
  const latestVouchers = useMemo(
    () => sortedVouchers.slice(0, 5),
    [sortedVouchers]
  );

  // Generate notifications dynamically based on voucherList data
  const notifications = useMemo(
    () =>
      latestVouchers.map((voucher) => ({
        id: voucher.id,
        title: voucher.title,
        description: voucher.description,
        isSeen: voucher.isSeen,
        messageId: voucher.messageId,
      })),
    [latestVouchers]
  );

  const notificationContent = (
    <div className="w-52 rounded-lg shadow-lg shadow-gray-200 cursor-pointer md:flex">
      {notifications.map((notification) => (
        <div
          key={notification.id}
          className="py-3 border-b last:border-b-2 rounded-lg p-3 border-b-gray-200 
           bg-white text-gray-800
           flex items-center justify-between"
          onClick={() => {
            try {
              seen({ id: notification.messageId });
              navigate(`/voucher/${notification.id}`);
            } catch (err: any) {
              // console.log("error", err);
            }
          }}
        >
          <div>
            <strong className="block font-semibold text-sm">
              {notification.title}
            </strong>
            <p className="mt-1 text-xs">{notification.description}</p>
          </div>
        </div>
      ))}

      <div
        className="text-gray-600 text-center py-2 cursor-pointer"
        onClick={handleSeeMoreClick}
      >
        {vouchersWithStatusRedeemed.length == 0 ? (
          <>No New Notification.</>
        ) : (
          <div
            className="text-gray-600 text-center py-2 cursor-pointer"
            onClick={() => navigate("/voucher/")}
          >
            see more
          </div>
        )}
      </div>
    </div>
  );

  const content = (
    <div style={{ color: "black", width: 200 }}>
      <div
        onClick={() => navigate("/profile")}
        style={{
          display: "flex",
          gap: 12,
          alignItems: "center",
          borderBottom: "1px solid #eee",
          paddingBottom: 5,
          marginTop: 15,
          cursor: "pointer",
        }}
      >
        <svg
          width="18"
          height="15"
          viewBox="0 0 18 15"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M17.0703 10.9766L17.7812 11.3867C17.8633 11.4141 17.8906 11.5234 17.8633 11.6055C17.6719 12.207 17.3711 12.7266 16.9609 13.1914C16.9062 13.2461 16.7969 13.2734 16.7148 13.2188L16.0039 12.8086C15.7031 13.0547 15.375 13.2734 14.9922 13.3828V14.2031C14.9922 14.3125 14.9375 14.3945 14.8555 14.3945C14.2539 14.5312 13.6523 14.5312 13.0234 14.3945C12.9414 14.3945 12.8867 14.3125 12.8867 14.2031V13.3828C12.5039 13.2734 12.1758 13.0547 11.875 12.8086L11.1641 13.2188C11.082 13.2734 10.9727 13.2461 10.918 13.1914C10.5078 12.7266 10.1797 12.207 10.0156 11.6055C9.98828 11.5234 10.0156 11.4141 10.0977 11.3867L10.8086 10.9766C10.7266 10.5938 10.7266 10.1836 10.8086 9.80078L10.0977 9.39062C10.0156 9.36328 9.98828 9.25391 10.0156 9.17188C10.1797 8.57031 10.5078 8.05078 10.918 7.58594C10.9727 7.53125 11.082 7.50391 11.1641 7.55859L11.875 7.96875C12.1758 7.72266 12.5039 7.50391 12.8867 7.39453V6.57422C12.8867 6.46484 12.9414 6.38281 13.0234 6.38281C13.625 6.24609 14.2266 6.24609 14.8555 6.38281C14.9375 6.38281 14.9922 6.46484 14.9922 6.57422V7.39453C15.375 7.50391 15.7031 7.72266 16.0039 7.96875L16.7148 7.55859C16.7969 7.50391 16.9062 7.53125 16.9609 7.58594C17.3711 8.05078 17.6719 8.57031 17.8633 9.17188C17.8906 9.25391 17.8633 9.36328 17.7812 9.39062L17.0703 9.80078C17.1523 10.1836 17.1523 10.5938 17.0703 10.9766ZM13.9531 11.7148C14.6641 11.7148 15.2656 11.1133 15.2656 10.375C15.2656 9.66406 14.6641 9.0625 13.9531 9.0625C13.2148 9.0625 12.6133 9.66406 12.6133 10.375C12.6133 11.1133 13.2148 11.7148 13.9531 11.7148ZM6.51562 7.75C4.57422 7.75 3.01562 6.19141 3.01562 4.25C3.01562 2.33594 4.57422 0.75 6.51562 0.75C8.42969 0.75 10.0156 2.33594 10.0156 4.25C10.0156 6.19141 8.42969 7.75 6.51562 7.75ZM12.0117 13.957V14.2031C12.0117 14.3125 12.0391 14.4219 12.0664 14.5312C11.8477 14.668 11.6016 14.75 11.3281 14.75H1.70312C0.964844 14.75 0.390625 14.1758 0.390625 13.4375V12.3164C0.390625 10.293 2.03125 8.625 4.05469 8.625H4.51953C5.12109 8.92578 5.80469 9.0625 6.51562 9.0625C7.22656 9.0625 7.88281 8.92578 8.48438 8.625H8.94922C9.05859 8.625 9.16797 8.65234 9.27734 8.67969C9.25 8.76172 9.19531 8.81641 9.16797 8.89844C9.03125 9.39062 9.22266 9.91016 9.66016 10.1562L9.87891 10.293C9.87891 10.3477 9.87891 10.4297 9.87891 10.4844L9.66016 10.6211C9.22266 10.8672 9.03125 11.3867 9.16797 11.8789C9.38672 12.5625 9.76953 13.2461 10.2891 13.7656C10.4805 13.9844 10.7812 14.1211 11.0547 14.1211C11.2461 14.1211 11.4375 14.0664 11.6016 13.9844L11.8203 13.8477C11.875 13.875 11.9297 13.9297 12.0117 13.957Z"
            fill="#EE8F00"
          />
        </svg>

        <div>My profile</div>
      </div>

      <div
        onClick={() => navigate("/changePassword")}
        style={{
          display: "flex",
          gap: 12,
          alignItems: "center",
          borderBottom: "1px solid #eee",
          paddingBottom: 5,
          marginTop: 15,
          cursor: "pointer",
        }}
      >
        <svg
          width="15"
          height="15"
          viewBox="0 0 15 15"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M14.3906 5.58984C14.3906 8.24219 12.2305 10.375 9.57812 10.375C9.25 10.375 8.94922 10.3477 8.67578 10.293L8.01953 11.0312C7.88281 11.168 7.71875 11.25 7.52734 11.25H6.51562V12.3438C6.51562 12.7266 6.21484 13 5.85938 13H4.76562V14.0938C4.76562 14.4766 4.46484 14.75 4.10938 14.75H1.04688C0.664062 14.75 0.390625 14.4766 0.390625 14.0938V11.9609C0.390625 11.7969 0.445312 11.6328 0.582031 11.4961L4.98438 7.09375C4.84766 6.60156 4.76562 6.10938 4.76562 5.5625C4.76562 2.91016 6.89844 0.777344 9.55078 0.75C12.2305 0.75 14.3906 2.91016 14.3906 5.58984ZM9.57812 4.25C9.57812 4.98828 10.1523 5.5625 10.8906 5.5625C11.6016 5.5625 12.2031 4.98828 12.2031 4.25C12.2031 3.53906 11.6016 2.9375 10.8906 2.9375C10.1523 2.9375 9.57812 3.53906 9.57812 4.25Z"
            fill="#EE8F00"
          />
        </svg>

        <div>Change password</div>
      </div>

      <div
        style={{
          display: "flex",
          gap: 12,
          alignItems: "center",
          borderBottom: "1px solid #eee",
          paddingBottom: 5,
          marginTop: 15,
          cursor: "pointer",
        }}
        onClick={() => {
          const token = localStorage.getItem("token");
          if (token) {
            signOut();
            localStorage.removeItem("token");
            navigate("/");
          }
        }}
      >
        <svg
          width="15"
          height="12"
          viewBox="0 0 15 12"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M13.9805 6.21484L9.38672 10.8086C8.97656 11.2188 8.26562 10.9453 8.26562 10.3438V7.71875H4.54688C4.16406 7.71875 3.89062 7.44531 3.89062 7.0625V4.4375C3.89062 4.08203 4.16406 3.78125 4.54688 3.78125H8.26562V1.15625C8.26562 0.582031 8.94922 0.28125 9.38672 0.691406L13.9805 5.28516C14.2266 5.55859 14.2266 5.96875 13.9805 6.21484ZM5.64062 10.6719C5.64062 10.8633 5.47656 11 5.3125 11H3.01562C1.56641 11 0.390625 9.82422 0.390625 8.375V3.125C0.390625 1.67578 1.56641 0.5 3.01562 0.5H5.3125C5.47656 0.5 5.64062 0.664062 5.64062 0.828125V1.92188C5.64062 2.11328 5.47656 2.25 5.3125 2.25H3.01562C2.52344 2.25 2.14062 2.66016 2.14062 3.125V8.375C2.14062 8.86719 2.52344 9.25 3.01562 9.25H5.3125C5.47656 9.25 5.64062 9.41406 5.64062 9.57812V10.6719Z"
            fill="#EE8F00"
          />
        </svg>

        <div>Logout</div>
      </div>
    </div>
  );

  return (
    <>
      <div
        className="hidden md:flex"
        style={{
          padding: 20,
          paddingTop: 47,
          gap: 12,
          // height: "100vh",
          // overflow: "auto", 
        }}
      >
        {/* Sidebar Section - Fixed */}
        <div
          style={{
            width: "15%",
            position: "fixed",  // Fix the sidebar
            top: 0,  // Align the sidebar to the top
            left: 0, // Align the sidebar to the left
            height: "100vh",  // Full height
            overflowY: "hidden", 
            overflowX: "hidden",
          }}
          className="print:hidden"
        >
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <Image src={img} width={50} height={50} preview={false} />
            <div
              style={{
                fontWeight: 700,
                fontSize: 30,
                textTransform: "uppercase",
              }}
            >
              <span style={{ color: "white" }}>shega</span>
              <span style={{ color: "#009602" }}>fre</span>
            </div>
          </div>
          <div
            style={{
              fontWeight: 400,
              fontSize: 11.88,
              color: "#9B9B9B",
              marginTop: 4,
            }}
          >
            {/* {user?.roleName} */}
          </div>
          <Menu
            onClick={onClick}
            style={{ width: 200, marginTop: 10 }}
            defaultSelectedKeys={["1"]}
            defaultOpenKeys={["sub1"]}
            mode="inline"
            items={menus}
          />
        </div>
  
        {/* Main Content Section - Scrollable */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            width: "85%",  // Make the main content take the remaining width
            marginLeft: "15%",  // Push the content to the right, alongside the sidebar
            gap: 12,
            overflowY: "auto", // Ensure the main content is scrollable
            height: "100vh",  // Full height to make the content scroll within the available space
          }}
        >
          <Header />
          {children}
        </div>
      </div>
  
      {/* Mobile view: Responsive Drawer for Sidebar */}
      <div className="w-full flex flex-col md:hidden p-3">
        <div className="flex justify-between items-center px-3">
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <MenuUnfoldOutlined
                style={{ color: "white" }}
                onClick={() => setOpenDrawer(!openDrawer)}
              />
              <Drawer
                width={"60%"}
                title="Shega Fre"
                placement={"left"}
                closable={false}
                onClose={() => setOpenDrawer(false)}
                open={openDrawer}
                key={"drawer"}
              >
                <Menu
                  onClick={onClick}
                  style={{ width: 200, marginTop: 10 }}
                  defaultSelectedKeys={["1"]}
                  defaultOpenKeys={["sub1"]}
                  mode="inline"
                  items={menus}
                />
              </Drawer>
              <Image src={img} width={50} height={50} preview={false} />
              <div
                style={{
                  fontWeight: 700,
                  fontSize: 20,
                  textTransform: "uppercase",
                }}
              >
                <span style={{ color: "white" }}>shega</span>
                <span style={{ color: "#009602" }}>fre</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-10 ">
            <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
              <Popover
                destroyTooltipOnHide
                content={notificationContent}
                trigger="click"
                className="cursor-pointer "
              >
                <Badge
                  count={
                    <div className="flex justify-center items-center text-xs w-[24px] h-[24px] rounded-full bg-green-200 ">
                      {vouchersWithStatusRedeemed.length > 9 ? (
                        <span>
                          9<sup>+</sup>
                        </span>
                      ) : (
                        vouchersWithStatusRedeemed.length
                      )}
                    </div>
                  }
                  offset={[0, 2]}
                  overflowCount={9}
                />
              </Popover>
            </div>
            <Popover
              placement="bottomLeft"
              title={<div style={{ color: "black" }}>{user?.fullName}</div>}
              content={content}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="45"
                height="45"
                viewBox="0 0 30 30"
                fill="none"
                stroke="gray"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="lucide lucide-circle-user"
              >
                <circle cx="12" cy="12" r="10" />
                <circle cx="12" cy="10" r="3" />
                <path d="M7 20.662V19a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v1.662" />
              </svg>
            </Popover>
          </div>
        </div>
        {children}
      </div>
    </>
  );
  
};

export default Layout;
